// ConfigurationSection.jsx
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDownIcon,
  TrashIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/solid";
import FormInput from "../../../components/controls/FormInput";
import Button from "../../../components/controls/Button";

const optionTypes = [
  { value: "color", labelFr: "Couleur", labelEn: "Color" },
  { value: "size", labelFr: "Taille", labelEn: "Size" },
  { value: "material", labelFr: "Matériau", labelEn: "Material" },
];

const ConfigurationSection = ({ formData, onChange, priceTypeId }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const showQuantityFields =
    priceTypeId !== 5 &&
    formData.productInventoryLimits?.inventoryMode !== "unlimited";

  /*const handleAddOption = () => {
    const newOption = {
      type: "",
      values: [{ nameFr: "", nameEn: "", color: "#000000", price: 0 }],
    };
    onChange("productConfigurations", [
      ...formData.productConfigurations,
      newOption,
    ]);
  };*/

  const handleOptionTypeChange = (index, newType) => {
    const updated = [...formData.productConfigurations];
    updated[index].type = newType;

    // Réinitialiser les valeurs si le type change
    updated[index].values = [
      {
        nameFr: "",
        nameEn: "",
        color: "#000000",
        price: 0,
        quantity: 0,
      },
    ];

    // ✅ Réinitialise totalQuantity si inventaire 'fixed'
    const inventoryMode = formData.productInventoryLimits?.inventoryMode;
    if (inventoryMode === "fixed") {
      formData.productInventoryLimits.totalQuantity = 0;
    }

    onChange("productConfigurations", updated);
  };

  useEffect(() => {
    if (formData?.productConfigurations?.length === 0) {
      onChange("productConfigurations", [
        {
          type: "",
          values: [
            { nameFr: "", nameEn: "", color: "#000000", price: 0, quantity: 0 },
          ],
        },
      ]);
    }
  }, [formData?.productConfigurations, onChange]);

  const handleValueChange = (optionIndex, valueIndex, field, value) => {
    let rawValue = value;

    if (field === "price") {
      const regex = /^\d{0,8}([.,]\d{0,2})?$/;

      // Autorise seulement les formats valides ou vide
      if (!regex.test(value) && value !== "") {
        return;
      }

      // Remplace la virgule par un point et convertit en nombre avec 2 décimales
      const numericValue = parseFloat(value?.toString().replace(",", "."));
      rawValue = isNaN(numericValue) ? 0 : parseFloat(numericValue.toFixed(2));
    }

    const updated = [...formData.productConfigurations];
    updated[optionIndex].values[valueIndex][field] = rawValue;
    onChange("productConfigurations", updated);
  };

  const handleAddValue = (optionIndex) => {
    const updated = [...formData.productConfigurations];
    updated[optionIndex].values.push({
      nameFr: "",
      nameEn: "",
      color: "#000000",
      price: 0,
    });
    onChange("productConfigurations", updated);
  };

  const handleRemoveValue = (optionIndex, valueIndex) => {
    const updated = [...formData.productConfigurations];
    updated[optionIndex].values.splice(valueIndex, 1);
    onChange("productConfigurations", updated);
  };

  const selectedTypes =
    formData?.productConfigurations?.map((conf) => conf.type) || [];

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h2 className="text-xl font-bold text-primary">
          {t("modal.configurations")}
        </h2>
        {/*<button
          type="button"
          onClick={handleAddOption}
          className="bg-buttonHoverCyan text-bgCyan border border-borderCyan px-4 py-1.5 text-sm rounded-full hover:bg-hoverCyan hover:text-white transition"
        >
          + {t("modal.addOption")}
        </button>
        */}
      </div>

      {formData?.productConfigurations?.map((opt, optionIndex) => (
        <div
          key={optionIndex}
          className="space-y-4 border p-4 rounded-md bg-gray-100"
        >
          <div>
            <label className="block text-sm font-medium text-onWhite">
              {t("modal.optionType")}
            </label>
            <div className="relative">
              <select
                value={opt.type}
                onChange={(e) =>
                  handleOptionTypeChange(optionIndex, e.target.value)
                }
                className="input input-bordered w-full appearance-none"
              >
                <option value="">{t("modal.selectOptionType")}</option>
                {optionTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {lang === "fr" ? type.labelFr : type.labelEn}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute top-1/2 right-3 w-5 h-5 text-gray-400 transform -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {opt.type !== "" &&
            opt.values.map((val, valueIndex) => (
              <div className="flex flex-wrap gap-4 border p-4 rounded-md items-end">
                <div className="flex-[1_1_200px] min-w-0">
                  <FormInput
                    label={t("modal.optionValueFr")}
                    name="nameFr"
                    value={val.nameFr}
                    onChange={(name, value) =>
                      handleValueChange(optionIndex, valueIndex, name, value)
                    }
                    required
                    maxLength={25}
                    showMaxLength
                  />
                </div>
                <div className="flex-[1_1_200px] min-w-0">
                  <FormInput
                    label={t("modal.optionValueEn")}
                    name="nameEn"
                    value={val.nameEn}
                    onChange={(name, value) =>
                      handleValueChange(optionIndex, valueIndex, name, value)
                    }
                    required
                    maxLength={25}
                    showMaxLength
                  />
                </div>
                {opt.type === "color" && (
                  <div className="flex-[1_1_120px] min-w-0">
                    <label className="block text-sm font-medium text-onWhite mb-1">
                      {t("modal.optionColor")}
                    </label>
                    <input
                      type="color"
                      value={val.color || "#000000"}
                      onChange={(e) =>
                        handleValueChange(
                          optionIndex,
                          valueIndex,
                          "color",
                          e.target.value
                        )
                      }
                      className="w-full h-14 cursor-pointer border-none outline-none rounded-lg overflow-hidden bg-transparent"
                      style={{
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        appearance: "none",
                      }}
                    />
                  </div>
                )}
                <div className="flex-[1_1_120px] min-w-0">
                  <FormInput
                    label={t("modal.priceSurplus")}
                    name="price"
                    type="text"
                    value={val.price || 0}
                    onChange={(name, value) =>
                      handleValueChange(optionIndex, valueIndex, name, value)
                    }
                    onFocus={(e) => e.target.select()}
                    placeholder="$0.00"
                    min={0}
                    icon={
                      <CurrencyDollarIcon className="w-5 h-5 text-gray-500" />
                    }
                  />
                </div>
                {showQuantityFields && (
                  <div className="flex-[1_1_120px] min-w-0">
                    <FormInput
                      label={t("modal.maxQuantity")}
                      name="quantity"
                      type="number"
                      value={val.quantity || 0}
                      onChange={(name, value) =>
                        handleValueChange(
                          optionIndex,
                          valueIndex,
                          name,
                          parseInt(value)
                        )
                      }
                      onFocus={(e) => e.target.select()}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                )}
                <div className="h-[60px] flex items-center">
                  <button
                    type="button"
                    title="Supprimer cette valeur / Delete this value"
                    onClick={() => handleRemoveValue(optionIndex, valueIndex)}
                    className="text-sm text-red-500 hover:text-red-700 transition"
                  >
                    <TrashIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>
            ))}

          <div className="text-right">
            <Button
              color="primary"
              size="sm"
              className="px-2 py-1 font-semibold"
            >
              + {t("modal.addValue")}
            </Button>
          </div>
        </div>
      ))}
    </section>
  );
};

export default ConfigurationSection;
